/**
 * Replay-only fake data for /meet. The recorder never sees a real name,
 * note or input value on /meet: each text node and input value is replaced
 * at record time, so the replay keeps its layout while carrying none of the
 * original.
 *
 * - Untyped text becomes uniform filler ("lorem lorem lo") at the original's
 *   length rounded up to a fixed bucket. Nothing of the original survives:
 *   no letters, case, punctuation or word lengths.
 * - Text inside a `<Phi kind>` wrapper (components/meet/replay/phi) gets a
 *   realistic fake of that kind ("Alex Sample", a 555 number, an
 *   example.com address), built entirely from generated parts. A value that
 *   does not look like its kind (a phone number with letters) gets the
 *   untyped filler instead. No builder transforms the original.
 * - The same original gives the same fake until the meeting scope changes,
 *   through an in-memory table filled from crypto randomness: never a hash
 *   of the original, and never stored or sent.
 *
 * Every fake starts with the page's mark: FAKE_MARK plus a per-tab random
 * nonce written in invisible characters. before_send
 * (clients/meet-replay-privacy) lets a string through only when it still
 * carries that exact mark, so provenance decides, never the value: page text
 * cannot forge the mark, and a real value that happens to equal a fake is
 * still replaced. The mark is stripped before upload.
 */

/** U+2063 INVISIBLE SEPARATOR: marks a replay fake until before_send strips it. */
export const FAKE_MARK = '⁣'

export type PhiKind =
  | 'name'
  | 'age'
  | 'date'
  | 'phone'
  | 'email'
  | 'number'
  | 'text'

export const PHI_KINDS: readonly PhiKind[] = [
  'name',
  'age',
  'date',
  'phone',
  'email',
  'number',
  'text',
]

const FIRST_NAMES = [
  'Alex',
  'Sam',
  'Jordan',
  'Riley',
  'Casey',
  'Morgan',
  'Jamie',
  'Robin',
]
const LAST_NAMES = ['Sample', 'Example', 'Placeholder', 'Testperson', 'Demo']
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
const DIGITS = '0123456789'

// The per-tab random state. Seeded once from crypto randomness, held only in
// this closure: never exported, logged, stored or sent.
let state: Uint32Array | null = null

const seed = () => {
  const words = new Uint32Array(4)
  const cryptoApi =
    typeof globalThis !== 'undefined'
      ? (globalThis.crypto as Crypto | undefined)
      : undefined
  if (cryptoApi?.getRandomValues) cryptoApi.getRandomValues(words)
  else
    for (let i = 0; i < 4; i++)
      words[i] = Math.floor(Math.random() * 0x100000000)
  if (!words.some(Boolean)) words[0] = 1
  return words
}

/** xorshift128: fast, and only ever seeded from crypto randomness. */
const nextRandom = () => {
  if (!state) state = seed()
  let t = state[3]
  const s = state[0]
  state[3] = state[2]
  state[2] = state[1]
  state[1] = s
  t ^= t << 11
  t ^= t >>> 8
  state[0] = t ^ s ^ (s >>> 19)
  return state[0] / 0x100000000
}

function pick(items: string): string
function pick<T>(items: readonly T[]): T
function pick<T>(items: readonly T[] | string): T | string {
  return items[Math.floor(nextRandom() * items.length)]
}
const randomInt = (min: number, max: number) =>
  min + Math.floor(nextRandom() * (max - min + 1))

// The invisible characters the nonce is written in (two bits each).
const NONCE_SYMBOLS = ['\u200b', '\u200c', '\u200d', '\u2060']
const NONCE_LENGTH = 16

// original → fake for the current meeting scope, and this scope's mark.
// Capped so a long call cannot grow it without bound; past the cap a repeat
// may get a new fake, which only costs consistency.
const MAX_ENTRIES = 20_000
const fakes = new Map<string, string>()
let mark: string | null = null

/** The page's mark: FAKE_MARK and a random nonce, invisible. */
const pageMark = () => {
  if (!mark)
    mark =
      FAKE_MARK +
      Array.from({ length: NONCE_LENGTH }, () => pick(NONCE_SYMBOLS)).join('')
  return mark
}

/**
 * Forgets every fake and draws a new mark. Called whenever the replay's
 * meeting scope changes, so nothing generated for one meeting is accepted in
 * another.
 */
export const resetReplayFakes = () => {
  fakes.clear()
  mark = null
}

/** Tests only: also draw a new random state. */
export const resetReplayFakesForTests = () => {
  resetReplayFakes()
  state = null
}

/** The length a fake of `length` characters is padded up to. */
export const bucketLength = (length: number) => {
  const step = length <= 16 ? 4 : length <= 64 ? 8 : 16
  return Math.max(step, Math.ceil(length / step) * step)
}

const FILLER = 'lorem '

/**
 * Uniform filler at the bucketed length of `value`. Only a leading or
 * trailing space is kept, for inline spacing; nothing else of the value.
 */
const filler = (value: string) => {
  const lead = /^\s/.test(value) ? ' ' : ''
  const trail = /\s$/.test(value) && value.trim() ? ' ' : ''
  const length = bucketLength(value.trim().length)
  const body = FILLER.repeat(Math.ceil(length / FILLER.length) + 1)
    .slice(0, length)
    .trimEnd()
  return `${lead}${body}${trail}`
}

const digits = (count: number) =>
  Array.from({ length: count }, () => pick(DIGITS)).join('')
const pad2 = (value: number) => String(value).padStart(2, '0')

const fakeName = () => `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`

// Each typed builder returns a whole generated value, or null when the
// original does not look like its kind (then the untyped filler is used).
const fakeAge = (value: string) =>
  /^\d{1,3}$/.test(value.trim()) ? String(randomInt(21, 79)) : null

const fakeDate = (value: string) => {
  const text = value.trim()
  const year = randomInt(2020, 2027)
  const month = randomInt(1, 12)
  const day = randomInt(1, 28)
  if (/^\d{4}-\d{2}-\d{2}$/.test(text))
    return `${year}-${pad2(month)}-${pad2(day)}`
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(text)) return `${month}/${day}/${year}`
  if (/^[A-Za-z]{3,9}\.? \d{1,2}(, \d{4})?$/.test(text)) {
    const name = MONTHS[month - 1]
    const short = /^[A-Za-z]{3}\.? /.test(text) ? name.slice(0, 3) : name
    return text.includes(',') ? `${short} ${day}, ${year}` : `${short} ${day}`
  }
  return null
}

const fakePhone = (value: string) =>
  /^[\d\s().+-]{7,20}$/.test(value.trim()) &&
  (value.match(/\d/g) || []).length >= 7
    ? `(555) 01${digits(1)}-${digits(4)}`
    : null

const fakeEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+$/.test(value.trim())
    ? `${pick(FIRST_NAMES).toLowerCase()}.${pick(
        LAST_NAMES
      ).toLowerCase()}@example.com`
    : null

const fakeNumber = (value: string) =>
  /^[\d.,\s-]{1,20}$/.test(value.trim()) && /\d/.test(value)
    ? String(randomInt(10, 999))
    : null

const BUILDERS: Record<PhiKind, (value: string) => string | null> = {
  name: fakeName,
  age: fakeAge,
  date: fakeDate,
  phone: fakePhone,
  email: fakeEmail,
  number: fakeNumber,
  text: () => null,
}

/** Whether a value is a PHI kind this module knows. */
export const isPhiKind = (value: unknown): value is PhiKind =>
  typeof value === 'string' && (PHI_KINDS as string[]).includes(value)

/** Removes every fake mark (marker and nonce characters) from a string. */
export const stripFakeMark = (value: string) =>
  value.replace(/[\u2063\u200b\u200c\u200d\u2060]/g, '')

/**
 * The fake for one text value, carrying the page's mark. Whitespace-only
 * text is layout, not content, and is returned as it is. The same kind and
 * original give the same fake until the meeting scope changes.
 */
export const replayFake = (value: string, kind: PhiKind = 'text'): string => {
  const original = stripFakeMark(value)
  if (!original.trim()) return original
  const key = `${kind}\u0000${original}`
  let fake = fakes.get(key)
  if (fake === undefined) {
    fake = BUILDERS[kind](original) ?? filler(original)
    if (fakes.size >= MAX_ENTRIES) fakes.clear()
    fakes.set(key, fake)
  }
  return pageMark() + fake
}

/**
 * The unmarked fake when a value carries this page's exact mark (and no
 * other mark character), else null. Provenance, never the value, decides.
 */
export const markedFake = (value: string): string | null => {
  if (!mark || !value.startsWith(mark)) return null
  const fake = value.slice(mark.length)
  return stripFakeMark(fake) === fake ? fake : null
}

/** The PHI kind an element or its nearest `data-phi` ancestor declares. */
export const phiKindOf = (element: Element | null | undefined): PhiKind => {
  const kind = element?.closest?.('[data-phi]')?.getAttribute('data-phi')
  return isPhiKind(kind) ? kind : 'text'
}

/** The PHI kind an input's type implies, else its `data-phi` kind. */
export const inputKindOf = (element: Element | null | undefined): PhiKind => {
  const type = (element as HTMLInputElement | null)?.type?.toLowerCase()
  if (type === 'email') return 'email'
  if (type === 'tel') return 'phone'
  if (type === 'date' || type === 'datetime-local' || type === 'month')
    return 'date'
  if (type === 'number') return 'number'
  return phiKindOf(element)
}
